/** Keys that belong to list pagination/sorting, not item export filters. */
const SKIP_LIST_KEYS = new Set(['limit', 'offset', 'ordering']);

/**
 * Map customer-invoice list filter keys → /api/customerinvoiceitems/ params.
 * ``status`` becomes ``invoice_document_status`` so it does not collide with
 * the line-item ``status`` field on the items API.
 */
export const CUSTOMER_INVOICE_LIST_TO_ITEMS = {
    status: 'invoice_document_status',
    invoice_status: 'invoice_status',
    paid: 'invoice_paid',
    billed: 'invoice_billed',
    credit: 'invoice_credit',
    billingaccount: 'invoice_billingaccount',
    invoice_date_min: 'invoice_date_min',
    invoice_date_max: 'invoice_date_max',
    invoice_date_preset: 'invoice_date_preset',
    due_date_min: 'due_date_min',
    due_date_max: 'due_date_max',
    due_date_preset: 'due_date_preset',
    sent: 'invoice_sent',
    invoice_type: 'invoice_type',
    search: 'invoice_search',
    due: 'invoice_due',
    accounting_period: 'invoice_accounting_period',
    assigned_to: 'invoice_assigned_to',
    payment_method: 'invoice_payment_method',
};

/**
 * Map provider-invoice list filter keys → /api/providerinvoiceitems/ params.
 */
export const PROVIDER_INVOICE_LIST_TO_ITEMS = {
    status: 'invoice_document_status',
    invoice_status: 'invoice_status',
    paid: 'invoice_paid',
    billed: 'invoice_billed',
    credit: 'invoice_credit',
    billingaccount: 'invoice_billingaccount',
    invoice_date_min: 'invoice_date_min',
    invoice_date_max: 'invoice_date_max',
    invoice_date_preset: 'invoice_date_preset',
    due_date_min: 'due_date_min',
    due_date_max: 'due_date_max',
    due_date_preset: 'due_date_preset',
    sent: 'invoice_sent',
    invoice_type: 'invoice_type',
    search: 'invoice_search',
    due: 'invoice_due',
    accounting_period: 'invoice_accounting_period',
    provider: 'invoice_provider',
    order: 'invoice_order',
};

/**
 * Build query params for invoice-line CSV export from invoice list filters.
 *
 * @param {Record<string, unknown>} listFilters
 * @param {Record<string, string>} keyMap
 * @returns {Record<string, unknown>}
 */
export function mapInvoiceListFiltersToItemFilters(listFilters, keyMap) {
    const filters = {};
    for (const [key, value] of Object.entries(listFilters || {})) {
        if (SKIP_LIST_KEYS.has(key)) {
            continue;
        }
        if (value === null || value === undefined || value === '') {
            continue;
        }
        const dest = keyMap[key];
        if (!dest) {
            continue;
        }
        filters[dest] = value;
    }
    return filters;
}

/**
 * Build items CSV ``ordering`` from the invoice-list sort.
 *
 * Invoice aliases are kept as-is (the items API maps them to ``invoice__*``).
 * ``position`` then ``id`` always follow so lines stay grouped by invoice and
 * keep their in-invoice order.
 *
 * @param {string|null|undefined} listOrdering
 * @param {{ fallback?: string }} [options]
 * @returns {string}
 */
export function buildInvoiceItemsExportOrdering(listOrdering, options = {}) {
    const fallback = options.fallback || '-invoice_date';
    const reserved = new Set(['position', 'id', 'limit', 'offset']);
    const parts = String(listOrdering || fallback)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
        .filter((part) => !reserved.has(part.replace(/^-/, '')));
    if (!parts.length) {
        parts.push(fallback);
    }
    parts.push('position', 'id');
    return parts.join(',');
}
